// A meeting's transcript as one readable document, with search, Download and Save as PDF.
import { Button, EmptyState, ErrorState, Field, InlineMessage, Input, MeetingIcon, Spinner } from '@olytic/foundation';
import { Download, Printer, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import { matchingLines, type MeetingTranscript, transcriptFileName, transcriptText } from './transcript';

export async function loadTranscript(meetingId: string): Promise<MeetingTranscript> {
  const res = await authenticatedFetch(`/api/meetings/${encodeURIComponent(meetingId)}/transcript`);
  const body = (await res.json().catch(() => null)) as (MeetingTranscript & { error?: string }) | null;
  if (!res.ok || !body) throw new Error(body?.error ?? `Could not load (${res.status}).`);
  return body;
}

function download(text: string, fileName: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Opens the transcript alone in a print window, where the browser's Save as PDF makes the file. */
function print(title: string, heading: string, text: string) {
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>` +
      '<style>body{font:12pt/1.55 Georgia,serif;margin:2.2cm;color:#111}h1{font:600 15pt/1.3 system-ui,sans-serif;margin:0 0 1.2em}pre{white-space:pre-wrap;font:inherit;margin:0}</style>' +
      `</head><body><h1>${escapeHtml(heading)}</h1><pre>${escapeHtml(text)}</pre></body></html>`,
  );
  win.document.close();
  win.focus();
  win.print();
}

export function TranscriptTab({ meetingId, title, date }: { meetingId: string; title: string; date: string | null }): JSX.Element {
  const [attempt, setAttempt] = useState(0);
  const [query, setQuery] = useState('');
  const [load, setLoad] = useState<{ state: 'loading' } | { state: 'error'; message: string } | { state: 'ready'; transcript: MeetingTranscript }>({ state: 'loading' });

  useEffect(() => {
    let live = true;
    setLoad({ state: 'loading' });
    loadTranscript(meetingId)
      .then((transcript) => { if (live) setLoad({ state: 'ready', transcript }); })
      .catch((e: unknown) => { if (live) setLoad({ state: 'error', message: e instanceof Error ? e.message : 'Could not load.' }); });
    return () => { live = false; };
  }, [meetingId, attempt]);

  if (load.state === 'loading') return <Spinner label="Loading the transcript…" />;
  if (load.state === 'error') return <ErrorState title="Could not load the transcript" description={load.message} onRetry={() => setAttempt((n) => n + 1)} />;
  return <TranscriptBody transcript={load.transcript} title={title} date={date} query={query} onQueryChange={setQuery} />;
}

function TranscriptBody({ transcript, title, date, query, onQueryChange }: {
  transcript: MeetingTranscript; title: string; date: string | null; query: string; onQueryChange: (query: string) => void;
}): JSX.Element {
  const text = transcriptText(transcript);
  const matches = useMemo(() => matchingLines(text, query), [text, query]);
  if (!text.trim()) {
    return <EmptyState icon={<MeetingIcon />} title="No transcript" description="This meeting has no transcript yet." />;
  }
  const heading = [title || 'Meeting', date].filter(Boolean).join(' · ');

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="neutral" size="sm" onClick={() => download(text, transcriptFileName(title, date, 'txt'))}>
          <Download />Download
        </Button>
        <Button variant="neutral" size="sm" onClick={() => print(transcriptFileName(title, date, 'pdf').replace(/\.pdf$/, ''), heading, text)}>
          <Printer />Save as PDF
        </Button>
      </div>
      {transcript.ordered ? null : (
        <InlineMessage tone="info">
          This transcript came from the old meeting store, which didn't keep the order of its parts. They're shown as excerpts until the meeting is captured again.
        </InlineMessage>
      )}
      <Field
        label="Search Transcript"
        help={query.trim() ? `${matches.length} ${matches.length === 1 ? 'line has' : 'lines have'} every word.` : undefined}
        className="max-w-md"
      >
        <Input type="search" value={query} onChange={(e) => onQueryChange(e.target.value)} placeholder="Find words in this meeting" startSlot={<Search aria-hidden />} />
      </Field>
      <div className="max-w-prose whitespace-pre-wrap text-sm leading-relaxed text-[var(--fg)]">
        {query.trim() ? matches.join('\n\n') : text}
      </div>
    </div>
  );
}
